import { useEffect } from 'react'
import { Button, Checkbox, Label, Skeleton } from '@teach/ui'
import { useGetLessonBlocksQuery } from '@/features/editor/lessonsApi'
import {
  lessonTargets,
  type SlotPlan,
  type TargetRef,
} from '@/features/ai/images/lib/collectTargets'
import type { Lesson } from '@/types/lesson'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  /** Урок из детали курса — он приходит без блоков (общая манера редактора). */
  lesson: Omit<Lesson, 'blocks'>
  /** Колонка + слот оригинала курса: «второй язык» — не всегда `en`. */
  slot: SlotPlan
  checked: boolean
  onToggle: () => void
  onTargets: (targets: TargetRef[]) => void
}

/**
 * Строка урока в списке мест. Подписку на блоки держит САМА строка: хуки
 * нельзя звать в цикле, поэтому агрегирующего хука по всем урокам нет — тот
 * же приём, что у `JobPoller` с его `JobSubscription`.
 */
export const LessonTargetRow = ({ lesson, slot, checked, onToggle, onTargets }: Props) => {
  const m = useMessages(messages)
  const { data: blocks, isLoading, isError, refetch } = useGetLessonBlocksQuery(lesson.id)
  const targets = blocks ? lessonTargets(lesson, blocks, slot) : []
  // Подпись целей строкой: `lessonTargets` возвращает НОВЫЙ массив на каждый
  // рендер, и по ссылке эффект бил бы бесконечно. В подпись входят и признаки
  // `hasImage`/`willSkip`, а не одни ключи: после прогона места те же, но уже
  // нарисованные — по подписи из одних ключей наверх уехал бы устаревший
  // список, и автор заплатил бы за перерисовку повторно.
  const signature = targets.map((t) => `${t.key}|${t.hasImage}|${t.willSkip}`).join('~')

  useEffect(() => {
    if (blocks) onTargets(targets)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, Boolean(blocks)])

  if (isLoading) return <Skeleton className="h-9" />

  const drawnCount = targets.filter((t) => t.hasImage).length
  // §6: в слоте `en` цель без базовой картинки сервер пропустит — говорим об
  // этом заранее, чтобы пропуск в итоге не выглядел поломкой.
  const skipCount = targets.filter((t) => t.willSkip).length
  const empty = targets.length === 0
  return (
    <div className="flex items-center gap-3 rounded-md px-2 py-1.5">
      <Checkbox
        id={`lesson-${lesson.id}`}
        checked={checked}
        disabled={isError || empty}
        onCheckedChange={onToggle}
      />
      <Label htmlFor={`lesson-${lesson.id}`} className="flex-1 truncate">
        {lesson.name}
      </Label>
      {/* Упавший запрос блоков — ТРЕТЬЕ состояние, а не «промптов нет»: места
          у урока, возможно, есть, мы их просто не получили, и молчаливо
          выключенная строка соврала бы автору. Повтор — `refetch` того же
          хука, отдельного запроса тут заводить не надо. */}
      {isError ? (
        <>
          <span className="text-xs text-muted-foreground">{m.targetsFailed}</span>
          <Button variant="ghost" size="sm" onClick={() => void refetch()}>
            {m.retry}
          </Button>
        </>
      ) : (
        <span className="text-xs text-muted-foreground">
          {empty ? m.noPrompts : m.drawnHint(targets.length, drawnCount)}
          {skipCount > 0 ? m.willSkip(skipCount) : ''}
        </span>
      )}
    </div>
  )
}
